import {useState} from 'react';

import {Popover, PopoverContent, PopoverPortal, PopoverTrigger} from '@/components/core/Popover';
import { useTranslation } from '@/hooks/useTranslation';

import { Badge } from '../core/Badge';

const Status: Record<string, 'info' | 'success' | 'error' | 'warning' | 'neutral'> = {
    ABORTED: 'error',
    ACCEPTED: 'success',
    BLOCKED: 'info',
    CHANGED: 'neutral',
    DEFERRED: 'neutral',
    DELETING: 'warning',
    DELIVERED: 'info',
    ENDED: 'success',
    ERROR: 'error',
    ESTABLISHED: 'success',
    EVALUATING: 'warning',
    QUEUED: 'warning',
    EXCEPTION: 'warning',
    EXPORTING: 'info',
    FAILED: 'error',
    FATAL: 'error',
    FINISHED: 'success',
    IGNORED: 'neutral',
    IMPORTED: 'success',
    IMPORTING: 'info',
    INACTIVE: 'neutral',
    INITIALIZING: 'warning',
    INVITED: 'info',
    NEW: 'neutral',
    OPEN: 'warning',
    PENDING: 'info',
    PENDING_PROCESSING: 'neutral',
    PROCESSED: 'success',
    PROCESSING: 'info',
    READY: 'success',
    RECEIVED: 'info',
    REJECTED: 'warning',
    REVIEWED: 'error',
    RUNNING: 'info',
    SENDING: 'info',
    STARTED: 'info',
    SUCCESS: 'success',
    TIMED_OUT: 'error',
    UNEXPECTED_DELAY: 'warning',
    UPDATING: 'info',
    WARNING: 'warning'
};

export const StatusColumn = ({
    status,
    finalStatus,
    message,
}: {
    status: string;
    finalStatus?: string;
    message?: string;
}) => {
    const [open, setOpen] = useState(false);
    const { t } = useTranslation();
    const labelStatus = finalStatus ?? status;
    const displayStatus = finalStatus ? `${t(status)} \u2192 ${t(finalStatus)}` : t(status);

    return (
        <div className="tw-overflow-hidden tw-min-w-0">
            <Popover open={open}>
                <PopoverTrigger
                    asChild
                    onMouseEnter={() => message && setOpen(true)}
                    onMouseLeave={() => message && setOpen(false)}
                >
                    <div className="tw-inline-block tw-max-w-full">
                        <Badge variant={Status[labelStatus]}>{displayStatus}</Badge>
                    </div>
                </PopoverTrigger>
                {message && (
                    <PopoverPortal>
                        <PopoverContent
                            side="right"
                            sideOffset={-8}
                            className="tw-w-full tw-max-w-[30vw] tw-max-h-[50vh] tw-p-6 tw-overflow-y-auto tw-whitespace-pre-wrap"
                            onMouseEnter={() => setOpen(true)}
                            onMouseLeave={() => setOpen(false)}
                        >
                            <span autoFocus={true}>{message}</span>
                        </PopoverContent>
                    </PopoverPortal>
                )}
            </Popover>
        </div>
    );
};
